/**
 * Workflow Analysis Queue Service
 * 
 * Manages sequential processing of workflow status analysis to prevent race conditions
 * when multiple column updates trigger concurrent workflow analysis requests.
 */

import { userService } from './userService';

const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.includes('dev') || hostname.includes('development')) {
      return DEV_API_BASE;
    }
  }
  return PROD_API_BASE;
})();

interface WorkflowAnalysisRequest {
  id: string;
  creatorId: string;
  tableId: string;
  projectId: string;
  currentStatus: string;
  timestamp: number;
  priority: 'high' | 'normal' | 'low';
  retryCount: number;
  maxRetries: number;
}

interface QueueStatus {
  isProcessing: boolean;
  queueLength: number;
  currentRequest?: WorkflowAnalysisRequest;
  completedCount: number;
  errorCount: number;
}

class WorkflowAnalysisQueue {
  private queue: WorkflowAnalysisRequest[] = [];
  private isProcessing = false;
  private completedCount = 0;
  private errorCount = 0;
  private processingRequest: WorkflowAnalysisRequest | null = null;
  private listeners: Array<(status: QueueStatus) => void> = [];

  /**
   * Add a workflow analysis request to the queue
   */
  async enqueue(
    creatorId: string,
    tableId: string,
    projectId: string,
    currentStatus: string,
    priority: 'high' | 'normal' | 'low' = 'normal'
  ): Promise<string> {
    const requestId = `${creatorId}-${tableId}-${Date.now()}`;
    
    // Check if there's already a pending request for this creator/table combination
    const existingIndex = this.queue.findIndex(
      req => req.creatorId === creatorId && req.tableId === tableId
    );
    
    if (existingIndex !== -1) {
      // Replace existing request with newer one (higher timestamp wins)
      const existing = this.queue[existingIndex];
      if (Date.now() > existing.timestamp) {
        console.log(`🔄 [WorkflowQueue] Replacing existing request for ${creatorId}/${tableId}`);
        this.queue[existingIndex] = {
          id: requestId,
          creatorId,
          tableId,
          projectId,
          currentStatus,
          timestamp: Date.now(),
          priority,
          retryCount: 0,
          maxRetries: 3
        };
      } else {
        console.log(`⏭️ [WorkflowQueue] Skipping duplicate request for ${creatorId}/${tableId}`);
        return existing.id;
      }
    } else {
      // Add new request to queue
      const request: WorkflowAnalysisRequest = {
        id: requestId,
        creatorId,
        tableId,
        projectId,
        currentStatus,
        timestamp: Date.now(),
        priority,
        retryCount: 0,
        maxRetries: 3
      };
      
      // Insert based on priority
      if (priority === 'high') {
        this.queue.unshift(request);
      } else {
        this.queue.push(request);
      }
      
      console.log(`📥 [WorkflowQueue] Enqueued request ${requestId} for ${creatorId}/${tableId}`);
    }
    
    this.notifyListeners();
    
    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }
    
    return requestId;
  }

  /**
   * Process the queue sequentially
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing) {
      return;
    }
    
    this.isProcessing = true;
    this.notifyListeners();
    
    console.log(`🚀 [WorkflowQueue] Starting queue processing (${this.queue.length} items)`);
    
    while (this.queue.length > 0) {
      const request = this.queue.shift()!;
      this.processingRequest = request;
      this.notifyListeners();
      
      try {
        console.log(`🔄 [WorkflowQueue] Processing ${request.id} for ${request.creatorId}/${request.tableId}`);
        
        await this.executeWorkflowAnalysis(request);
        
        this.completedCount++;
        console.log(`✅ [WorkflowQueue] Completed ${request.id}`);
        
      } catch (error) {
        console.error(`❌ [WorkflowQueue] Failed ${request.id}:`, error);
        
        // Retry logic
        if (request.retryCount < request.maxRetries) {
          request.retryCount++;
          console.log(`🔄 [WorkflowQueue] Retrying ${request.id} (attempt ${request.retryCount})`);
          
          // Add back to queue with delay
          setTimeout(() => {
            this.queue.unshift(request);
            this.notifyListeners();
          }, 1000 * request.retryCount); // Exponential backoff
        } else {
          this.errorCount++;
          console.error(`💥 [WorkflowQueue] Max retries exceeded for ${request.id}`);
        }
      }
      
      this.processingRequest = null;
      this.notifyListeners();
      
      // Small delay between requests to prevent overwhelming the backend
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    this.isProcessing = false;
    this.notifyListeners();
    
    console.log(`🏁 [WorkflowQueue] Queue processing completed. Completed: ${this.completedCount}, Errors: ${this.errorCount}`);
  }

  /**
   * Execute the actual workflow analysis request
   */
  private async executeWorkflowAnalysis(request: WorkflowAnalysisRequest): Promise<void> {
    const token = await userService.getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/workflow-status-agent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        cognito_user_id: 'frontend-queue', // Will be extracted from JWT
        table_id: request.tableId,
        creator_id: request.creatorId,
        project_id: request.projectId,
        current_status: request.currentStatus,
        trigger: 'sequential_queue'
      })
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Workflow analysis quota exhausted');
      }
      throw new Error(`Workflow analysis failed: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Workflow analysis failed');
    }
  }

  /**
   * Get current queue status
   */
  getStatus(): QueueStatus {
    return {
      isProcessing: this.isProcessing,
      queueLength: this.queue.length,
      currentRequest: this.processingRequest || undefined,
      completedCount: this.completedCount,
      errorCount: this.errorCount
    };
  }

  /**
   * Subscribe to queue status changes
   */
  subscribe(listener: (status: QueueStatus) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of status changes
   */
  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach(listener => listener(status));
  }

  /**
   * Clear the queue (useful for cleanup)
   */
  clear(): void {
    this.queue = [];
    this.isProcessing = false;
    this.processingRequest = null;
    this.notifyListeners();
  }

  /**
   * Reset counters
   */
  reset(): void {
    this.completedCount = 0;
    this.errorCount = 0;
    this.notifyListeners();
  }

  /**
   * Get queue statistics
   */
  getStats(): {
    totalProcessed: number;
    successRate: number;
    averageQueueLength: number;
  } {
    const total = this.completedCount + this.errorCount;
    return {
      totalProcessed: total,
      successRate: total > 0 ? (this.completedCount / total) * 100 : 0,
      averageQueueLength: this.queue.length
    };
  }
}

// Export singleton instance
export const workflowAnalysisQueue = new WorkflowAnalysisQueue();
export default workflowAnalysisQueue;
